import React, { useState } from 'react';
import { Card, Tabs, Typography, Divider, Button, Toast } from '@douyinfe/semi-ui';
import { IconCode, IconCommand, IconCopy, IconTickCircle } from '@douyinfe/semi-icons';

const { TabPane } = Tabs;
const { Text, Title, Paragraph } = Typography;

const ClaudeDocs = () => {
    const [copiedCode, setCopiedCode] = useState('');

    const copyToClipboard = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedCode(id);
            Toast.success('已复制到剪贴板');
            setTimeout(() => setCopiedCode(''), 2000);
        } catch (err) {
            Toast.error('复制失败，请手动复制');
        }
    };

    const CodeBlock = ({ children, id }) => (
        <div className="cmd-block-wrapper">
            <pre className="cmd-block">
                <code>{children}</code>
            </pre>
            <Button
                icon={copiedCode === id ? <IconTickCircle /> : <IconCopy />}
                className="code-copy-btn"
                size="small"
                theme="borderless"
                onClick={() => copyToClipboard(children, id)}
            >
                {copiedCode === id ? '已复制' : '复制'}
            </Button>
        </div>
    );

    const InfoCard = ({ title, children, type = 'default' }) => (
        <div className={`info-card info-card--${type}`}>
            {title && <div className="info-card-title">{title}</div>}
            <div className="info-card-body">{children}</div>
        </div>
    );

    const WindowsContent = () => (
        <div className="tutorial-wrapper">
            <div className="tutorial-intro">
                <Paragraph style={{ fontSize: '15px', color: 'var(--semi-color-text-1)' }}>
                    本教程将指导您在 Windows 系统上配置 Claude Code 开发环境
                </Paragraph>
            </div>

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">📦</span>
                    安装 Node.js 运行时
                </Title>
                
                <Paragraph type="secondary">
                    首先需要安装 Node.js，这是运行 Claude Code 的基础环境
                </Paragraph>

                <div className="install-options">
                    <InfoCard title="推荐方式" type="primary">
                        <Paragraph>访问官方网站下载安装包：</Paragraph>
                        <a href="https://nodejs.org/" target="_blank" rel="noopener noreferrer" className="download-link">
                            nodejs.org
                        </a>
                        <Paragraph type="tertiary" style={{ marginTop: '12px' }}>
                            下载 LTS 版本，双击运行安装程序，按默认选项完成安装
                        </Paragraph>
                    </InfoCard>

                    <InfoCard title="命令行方式" type="secondary">
                        <Paragraph>如果已安装包管理器，可使用命令安装：</Paragraph>
                        <CodeBlock id="win-choco">choco install nodejs</CodeBlock>
                        <Text type="tertiary">或</Text>
                        <CodeBlock id="win-scoop">scoop install nodejs</CodeBlock>
                    </InfoCard>
                </div>

                <InfoCard type="tip">
                    <Text>💡 安装完成后，打开 PowerShell 输入以下命令验证：</Text>
                    <CodeBlock id="win-verify">node -v && npm -v</CodeBlock>
                    <Text type="success">看到版本号即表示安装成功</Text>
                </InfoCard>
            </div>

            <Divider margin="24px" />

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">⚡</span>
                    安装 Claude Code 工具
                </Title>

                <Paragraph type="secondary">
                    通过 npm 全局安装 Claude Code 命令行工具
                </Paragraph>

                <InfoCard type="command">
                    <CodeBlock id="win-install-claude">npm install -g @anthropic-ai/claude-code</CodeBlock>
                </InfoCard>

                <InfoCard type="tip">
                    <Text>检查是否安装成功：</Text>
                    <CodeBlock id="win-check-claude">claude --version</CodeBlock>
                </InfoCard>
            </div>

            <Divider margin="24px" />

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">🔧</span>
                    配置 API 连接
                </Title>

                <Paragraph type="secondary">
                    设置环境变量以连接到 API 服务
                </Paragraph>

                <div className="config-tabs">
                    <InfoCard title="临时配置（仅当前终端有效）" type="secondary">
                        <CodeBlock id="win-temp-config">{`$env:ANTHROPIC_BASE_URL = "https://shell.wyzlab.ai"
$env:ANTHROPIC_AUTH_TOKEN = "你的密钥"`}</CodeBlock>
                    </InfoCard>

                    <InfoCard title="永久配置（一次设置，永久生效）" type="primary">
                        <CodeBlock id="win-perm-config">{`[Environment]::SetEnvironmentVariable("ANTHROPIC_BASE_URL", "https://shell.wyzlab.ai", "User")
[Environment]::SetEnvironmentVariable("ANTHROPIC_AUTH_TOKEN", "你的密钥", "User")`}</CodeBlock>
                        <Text type="warning" style={{ marginTop: '8px' }}>
                            ⚠️ 设置后需要重启终端才能生效
                        </Text>
                    </InfoCard>
                </div>

                <InfoCard type="tip">
                    <Text>验证配置：</Text>
                    <CodeBlock id="win-verify-config">echo $env:ANTHROPIC_BASE_URL</CodeBlock>
                    <Text type="tertiary">应该输出：https://shell.wyzlab.ai</Text>
                </InfoCard>
            </div>

            <Divider margin="24px" />

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">🚀</span>
                    开始使用
                </Title>

                <InfoCard type="success">
                    <Paragraph>在任意目录打开终端，输入命令启动：</Paragraph>
                    <CodeBlock id="win-start">claude</CodeBlock>
                    <Paragraph type="tertiary" style={{ marginTop: '12px' }}>
                        首次使用会进行初始化，稍等片刻即可开始对话
                    </Paragraph>
                </InfoCard>
            </div>

            <div className="tutorial-section">
                <Title heading={6} style={{ marginTop: '24px' }}>
                    额外配置（可选）
                </Title>
                
                <InfoCard title="VSCode 插件配置" type="secondary">
                    <Paragraph>配置文件路径：<code>C:\Users\你的用户名\.claude\config.json</code></Paragraph>
                    <CodeBlock id="win-vscode-config">{`{
  "primaryApiKey": "你的密钥"
}`}</CodeBlock>
                </InfoCard>
            </div>

            <Divider margin="24px" />

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">📚</span>
                    进阶学习资源
                </Title>

                <Paragraph type="secondary">
                    更多教程和高级技巧，助你精通 Claude Code
                </Paragraph>

                <InfoCard type="primary">
                    <div className="resource-header">
                        <Text strong style={{ fontSize: '15px' }}>Claude 中文网 - 专业教程平台</Text>
                        <a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer" className="resource-link">
                            访问网站 →
                        </a>
                    </div>
                    <Paragraph type="tertiary" style={{ marginTop: '8px' }}>
                        提供完整的 Claude Code 中文教程、实用技巧和最佳实践指南
                    </Paragraph>
                </InfoCard>

                <div className="resources-grid">
                    <InfoCard title="📖 热门教程" type="secondary">
                        <ul className="resource-list">
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">完整使用指南</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">命令指南与快捷键</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">最佳实践与工作流程</a></li>
                        </ul>
                    </InfoCard>

                    <InfoCard title="💡 实用技巧" type="secondary">
                        <ul className="resource-list">
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">34个实用技巧集锦</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">16个高端玩法</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">效率提升秘籍</a></li>
                        </ul>
                    </InfoCard>

                    <InfoCard title="🔄 工具对比" type="secondary">
                        <ul className="resource-list">
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">Claude Code vs Cursor</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">功能对比分析</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">迁移指南</a></li>
                        </ul>
                    </InfoCard>
                </div>
            </div>
        </div>
    );

    const MacOSContent = () => (
        <div className="tutorial-wrapper">
            <div className="tutorial-intro">
                <Paragraph style={{ fontSize: '15px', color: 'var(--semi-color-text-1)' }}>
                    本教程将指导您在 macOS 系统上配置 Claude Code 开发环境
                </Paragraph>
            </div>

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">📦</span>
                    安装 Node.js 运行时
                </Title>
                
                <Paragraph type="secondary">
                    首先需要安装 Node.js，这是运行 Claude Code 的基础环境
                </Paragraph>

                <div className="install-options">
                    <InfoCard title="推荐方式（使用 Homebrew）" type="primary">
                        <Paragraph>如果已安装 Homebrew，直接运行：</Paragraph>
                        <CodeBlock id="mac-brew">brew update && brew install node</CodeBlock>
                    </InfoCard>

                    <InfoCard title="官网下载" type="secondary">
                        <Paragraph>访问官方网站下载 macOS 安装包：</Paragraph>
                        <a href="https://nodejs.org/" target="_blank" rel="noopener noreferrer" className="download-link">
                            nodejs.org
                        </a>
                        <Paragraph type="tertiary" style={{ marginTop: '12px' }}>
                            下载 LTS 版本，打开 .pkg 文件按提示安装
                        </Paragraph>
                    </InfoCard>
                </div>

                <InfoCard type="tip">
                    <Text>💡 安装完成后，打开终端输入以下命令验证：</Text>
                    <CodeBlock id="mac-verify">node -v && npm -v</CodeBlock>
                    <Text type="success">看到版本号即表示安装成功</Text>
                </InfoCard>
            </div>

            <Divider margin="24px" />

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">⚡</span>
                    安装 Claude Code 工具
                </Title>

                <Paragraph type="secondary">
                    通过 npm 全局安装 Claude Code 命令行工具
                </Paragraph>

                <InfoCard type="command">
                    <CodeBlock id="mac-install-claude">npm install -g @anthropic-ai/claude-code</CodeBlock>
                    <Text type="tertiary" style={{ marginTop: '8px' }}>
                        如遇权限问题，在命令前加 sudo
                    </Text>
                </InfoCard>

                <InfoCard type="tip">
                    <Text>检查是否安装成功：</Text>
                    <CodeBlock id="mac-check-claude">claude --version</CodeBlock>
                </InfoCard>
            </div>

            <Divider margin="24px" />

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">🔧</span>
                    配置 API 连接
                </Title>

                <Paragraph type="secondary">
                    设置环境变量以连接到 API 服务
                </Paragraph>

                <div className="config-tabs">
                    <InfoCard title="临时配置（仅当前终端有效）" type="secondary">
                        <CodeBlock id="mac-temp-config">{`export ANTHROPIC_BASE_URL="https://shell.wyzlab.ai"
export ANTHROPIC_AUTH_TOKEN="你的密钥"`}</CodeBlock>
                    </InfoCard>

                    <InfoCard title="永久配置（推荐）" type="primary">
                        <Paragraph>添加到 Shell 配置文件（zsh 或 bash）：</Paragraph>
                        <CodeBlock id="mac-perm-config">{`# zsh (macOS 默认)
echo 'export ANTHROPIC_BASE_URL="https://shell.wyzlab.ai"' >> ~/.zshrc
echo 'export ANTHROPIC_AUTH_TOKEN="你的密钥"' >> ~/.zshrc
source ~/.zshrc

# bash
echo 'export ANTHROPIC_BASE_URL="https://shell.wyzlab.ai"' >> ~/.bash_profile
echo 'export ANTHROPIC_AUTH_TOKEN="你的密钥"' >> ~/.bash_profile
source ~/.bash_profile`}</CodeBlock>
                    </InfoCard>
                </div>

                <InfoCard type="tip">
                    <Text>验证配置：</Text>
                    <CodeBlock id="mac-verify-config">echo $ANTHROPIC_BASE_URL</CodeBlock>
                    <Text type="tertiary">应该输出：https://shell.wyzlab.ai</Text>
                </InfoCard>
            </div>

            <Divider margin="24px" />

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">🚀</span>
                    开始使用
                </Title>

                <InfoCard type="success">
                    <Paragraph>在任意目录打开终端，输入命令启动：</Paragraph>
                    <CodeBlock id="mac-start">claude</CodeBlock>
                    <Paragraph type="tertiary" style={{ marginTop: '12px' }}>
                        首次使用会进行初始化，稍等片刻即可开始对话
                    </Paragraph>
                </InfoCard>
            </div>

            <div className="tutorial-section">
                <Title heading={6} style={{ marginTop: '24px' }}>
                    额外配置（可选）
                </Title>
                
                <InfoCard title="VSCode 插件配置" type="secondary">
                    <Paragraph>配置文件路径：<code>~/.claude/config.json</code></Paragraph>
                    <CodeBlock id="mac-vscode-config">{`{
  "primaryApiKey": "你的密钥"
}`}</CodeBlock>
                </InfoCard>
            </div>

            <Divider margin="24px" />

            <div className="tutorial-section">
                <Title heading={5} className="section-heading">
                    <span className="heading-icon">📚</span>
                    进阶学习资源
                </Title>

                <Paragraph type="secondary">
                    更多教程和高级技巧，助你精通 Claude Code
                </Paragraph>

                <InfoCard type="primary">
                    <div className="resource-header">
                        <Text strong style={{ fontSize: '15px' }}>Claude 中文网 - 专业教程平台</Text>
                        <a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer" className="resource-link">
                            访问网站 →
                        </a>
                    </div>
                    <Paragraph type="tertiary" style={{ marginTop: '8px' }}>
                        提供完整的 Claude Code 中文教程、实用技巧和最佳实践指南
                    </Paragraph>
                </InfoCard>

                <div className="resources-grid">
                    <InfoCard title="📖 热门教程" type="secondary">
                        <ul className="resource-list">
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">完整使用指南</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">命令指南与快捷键</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">最佳实践与工作流程</a></li>
                        </ul>
                    </InfoCard>

                    <InfoCard title="💡 实用技巧" type="secondary">
                        <ul className="resource-list">
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">34个实用技巧集锦</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">16个高端玩法</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">效率提升秘籍</a></li>
                        </ul>
                    </InfoCard>

                    <InfoCard title="🔄 工具对比" type="secondary">
                        <ul className="resource-list">
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">Claude Code vs Cursor</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">功能对比分析</a></li>
                            <li><a href="https://www.claude-cn.org/" target="_blank" rel="noopener noreferrer">迁移指南</a></li>
                        </ul>
                    </InfoCard>
                </div>
            </div>
        </div>
    );

    return (
        <div className="claude-docs-page">
            <Card
                className="glass-card docs-card"
                headerLine
                bodyStyle={{ padding: '32px' }}
                title={
                    <div className="card-title">
                        <span className="card-title-text">Claude Code 配置指南</span>
                        <span className="card-subtitle">快速部署 AI 编程助手到本地环境</span>
                    </div>
                }
            >
                <Tabs type="line" defaultActiveKey="windows" className="platform-tabs">
                    <TabPane 
                        tab={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <IconCommand />
                                Windows
                            </span>
                        } 
                        itemKey="windows"
                    >
                        <WindowsContent />
                    </TabPane>
                    <TabPane 
                        tab={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <IconCode />
                                macOS
                            </span>
                        } 
                        itemKey="macos"
                    >
                        <MacOSContent />
                    </TabPane>
                </Tabs>
            </Card>
        </div>
    );
};

export default ClaudeDocs;
